// src/utils/firestorePaths.js
//
// Central builders for property-scoped Firestore paths.
//
// Properties are nested under their owning client:
//   Clients/{clientId}/properties/{propertyId}/...
//
// Every property-scoped read/write in the app should go through these helpers
// instead of hand-writing `collection(firestore, "properties", ...)`. That
// keeps the client/property nesting in exactly one place.
//
// Usage:
//   import { propertyDoc, propertyCollection } from "../utils/firestorePaths";
//   const roomsRef = propertyCollection(clientId, propertyId, "units");
//   const roomRef  = propertyDoc(clientId, propertyId, "units", roomId);

import { collection, doc } from "firebase/firestore";
import { firestore } from "../firebase";

export const CLIENTS_COLLECTION = "Clients";
export const PROPERTIES_COLLECTION = "properties";

function assertIds(clientId, propertyId) {
  if (!clientId) {
    throw new Error(
      `firestorePaths: missing clientId (propertyId="${propertyId}"). ` +
        `Every property-scoped path now needs the owning clientId.`
    );
  }
  if (!propertyId) {
    throw new Error(`firestorePaths: missing propertyId (clientId="${clientId}").`);
  }
}

/**
 * Reference to a document under a property.
 *   propertyDoc(clientId, propertyId)                       -> the property doc
 *   propertyDoc(clientId, propertyId, "units", roomId)      -> a room doc
 */
export function propertyDoc(clientId, propertyId, ...segments) {
  assertIds(clientId, propertyId);
  return doc(
    firestore,
    CLIENTS_COLLECTION,
    clientId,
    PROPERTIES_COLLECTION,
    propertyId,
    ...segments
  );
}

/**
 * Reference to a subcollection under a property.
 *   propertyCollection(clientId, propertyId, "units")       -> the units collection
 *   propertyCollection(clientId, propertyId, "units", roomId, "images")
 */
export function propertyCollection(clientId, propertyId, ...segments) {
  assertIds(clientId, propertyId);
  return collection(
    firestore,
    CLIENTS_COLLECTION,
    clientId,
    PROPERTIES_COLLECTION,
    propertyId,
    ...segments
  );
}

/** Reference to a client document: Clients/{clientId}. */
export function clientDoc(clientId, ...segments) {
  if (!clientId) throw new Error("firestorePaths: missing clientId.");
  return doc(firestore, CLIENTS_COLLECTION, clientId, ...segments);
}

/** Reference to the properties collection under one client. */
export function clientPropertiesCollection(clientId) {
  if (!clientId) throw new Error("firestorePaths: missing clientId.");
  return collection(firestore, CLIENTS_COLLECTION, clientId, PROPERTIES_COLLECTION);
}
